import React, { useState, useEffect } from 'react';
import { snapshotsApi } from '../services/api';
import { PriceSnapshot } from '../types';
import { useTimezone } from '../context/TimezoneContext';
import { formatDateTime } from '../utils/datetime';
import { toISOWithTimezone } from '../utils/datetime';

const Snapshots: React.FC = () => {
  const [snapshots, setSnapshots] = useState<PriceSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [scryfallInfo, setScryfallInfo] = useState<any>(null);
  const [loadingScryfallInfo, setLoadingScryfallInfo] = useState(false);
  const [autoDownloading, setAutoDownloading] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const { timezone } = useTimezone();

  const [formData, setFormData] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    // Format for datetime-local input: YYYY-MM-DDTHH:mm
    const dateTimeLocal = now.toISOString().slice(0, 16);
    
    return {
      snapshot_date: dateTimeLocal,
      description: `Snapshot ${year}-${month}-${day}`,
      file: null as File | null,
    };
  });

  useEffect(() => {
    loadSnapshots();
    loadScryfallInfo();
  }, []);

  const loadSnapshots = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await snapshotsApi.getAll();
      setSnapshots(response.data);
    } catch (error) {
      console.error('Failed to load snapshots:', error);
      setError('Failed to load snapshots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadScryfallInfo = async () => {
    try {
      setLoadingScryfallInfo(true);
      const response = await snapshotsApi.getScryfallLatestInfo();
      setScryfallInfo(response.data);
    } catch (error) {
      console.error('Failed to load Scryfall info:', error);
    } finally {
      setLoadingScryfallInfo(false);
    }
  };

  const handleAutoDownload = async () => {
    if (!window.confirm('This will download and process ~500MB of data. This may take several minutes. Continue?')) {
      return;
    }
    
    try {
      setAutoDownloading(true);
      setCreateError(null);
      await snapshotsApi.autoDownloadScryfall();
      setCreateSuccess('Scryfall snapshot downloaded and processed successfully!');
      loadSnapshots();
      loadScryfallInfo();
    } catch (error: any) {
      console.error('Failed to auto-download:', error);
      setCreateError(error.response?.data?.detail || 'Failed to download snapshot.');
    } finally {
      setAutoDownloading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleCreateSnapshot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.file || !formData.snapshot_date || !formData.description) {
      setCreateError('Please fill in all fields and select a file.');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      setCreateSuccess(null);

      const formDataToSend = new FormData();
      formDataToSend.append('snapshot_date', toISOWithTimezone(formData.snapshot_date, timezone));
      formDataToSend.append('description', formData.description);
      formDataToSend.append('file', formData.file);

      await snapshotsApi.create(formDataToSend);
      
      setCreateSuccess(`Snapshot created successfully!`);
      
      // Reset form with fresh pre-filled values
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const dateTimeLocal = now.toISOString().slice(0, 16);

      setFormData({
        snapshot_date: dateTimeLocal,
        description: `Snapshot ${year}-${month}-${day}`,
        file: null
      });
      
      loadSnapshots(); // Reload snapshots
    } catch (error: any) {
      console.error('Failed to create snapshot:', error);
      setCreateError(error.response?.data?.detail || 'Failed to create snapshot. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (snapshotId: string) => {
    if (!window.confirm('Are you sure you want to delete this snapshot? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(snapshotId);
      setError(null);
      await snapshotsApi.delete(snapshotId);
      setSnapshots(snapshots.filter(s => s.id !== snapshotId));
    } catch (error: any) {
      console.error('Failed to delete snapshot:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete snapshot. Please try again.';
      setError(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading snapshots...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Price Snapshots</h1>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Auto-Download Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Quick Snapshot</h2>
          <p className="text-gray-600 mb-4">
            Automatically download and process the latest Scryfall card data
          </p>
          
          {createError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {createError}
            </div>
          )}

          {createSuccess && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {createSuccess}
            </div>
          )}
          
          {loadingScryfallInfo ? (
            <div className="text-gray-600">Loading Scryfall info...</div>
          ) : scryfallInfo ? (
            <>
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Latest Update:</span>
                    <p className="text-gray-900">{new Date(scryfallInfo.updated_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Download Size:</span>
                    <p className="text-gray-900">{formatBytes(scryfallInfo.size)}</p>
                  </div>
                </div>
                
                {scryfallInfo.snapshot_exists && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                    ⚠️ A snapshot for this date already exists
                  </div>
                )}
              </div>
              
              <button
                onClick={handleAutoDownload}
                disabled={autoDownloading || scryfallInfo.snapshot_exists}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {autoDownloading ? 'Downloading and Processing... (this may take several minutes)' : 'Download Latest Snapshot'}
              </button>
            </>
          ) : (
            <div className="text-red-600">Failed to load Scryfall information</div>
          )}
        </div>

        {/* Manual Upload Section (Collapsible) */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <button
            onClick={() => setShowManualUpload(!showManualUpload)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Manual Upload</h3>
              <p className="text-sm text-gray-600">Upload a Scryfall JSON file manually</p>
            </div>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${showManualUpload ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showManualUpload && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="pt-4">
                <form onSubmit={handleCreateSnapshot} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Snapshot Date *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.snapshot_date}
                        onChange={(e) => setFormData({ ...formData, snapshot_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Auto-filled with current date and time (editable)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., January 2025 Scryfall Data"
                        required
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Auto-generated based on current date (editable)
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scryfall JSON File *
                    </label>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      required
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Upload the Scryfall default-cards JSON file (downloaded from Scryfall's bulk data API)
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={creating || !formData.file || !formData.snapshot_date || !formData.description}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {creating ? 'Creating Snapshot...' : 'Create Snapshot'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Snapshots List Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Existing Snapshots</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {snapshots.map((snapshot) => (
              <div
                key={snapshot.id}
                className="bg-gray-50 rounded-lg p-4 hover:shadow-lg transition-shadow"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{snapshot.description || 'Untitled Snapshot'}</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Date:</span>
                    <span className="text-sm font-medium">{formatDateTime(snapshot.snapshot_date, timezone)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Cards:</span>
                    <span className="text-sm font-medium">{snapshot.total_cards}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Status:</span>
                    <span className={`text-sm font-medium ${
                      snapshot.ingestion_status === 'completed' ? 'text-green-600' :
                      snapshot.ingestion_status === 'processing' ? 'text-yellow-600' :
                      snapshot.ingestion_status === 'failed' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {snapshot.ingestion_status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Created:</span>
                    <span className="text-sm font-medium">{formatDateTime(snapshot.created_at, timezone)}</span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => handleDelete(snapshot.id)}
                    disabled={deleting === snapshot.id}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting === snapshot.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {snapshots.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No snapshots found. Create your first snapshot above!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Snapshots;